import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { ArrowLeft, RefreshCcw, ShieldAlert } from "lucide-react";

import FullScreenLoader from "../components/common/FullScreenLoader";
import legupLogo from "../assets/legup-logo.svg";

import "./Callback.css";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

function getStoredAuthConnection() {
  return sessionStorage.getItem(AUTH_CONNECTION_KEY) || "";
}

export default function Callback() {
  const { isLoading, error, loginWithRedirect } = useAuth0();

  const searchParams = new URLSearchParams(window.location.search);

  const urlError = searchParams.get("error") || "";

  const urlErrorDescription = searchParams.get("error_description") || "";

  const errorMessage = urlErrorDescription || error?.message || "";

  const isBlocked =
    urlError === "unauthorized" &&
    errorMessage.toLowerCase().includes("blocked");

  /* =========================================================
     START LOGIN AGAIN
     ========================================================= */

  async function restartLogin() {
    try {
      const connection = getStoredAuthConnection();

      await loginWithRedirect({
        authorizationParams: {
          prompt: "login",

          ...(connection
            ? {
                connection,
              }
            : {}),
        },

        appState: {
          returnTo: "/post-login",
        },
      });
    } catch (err) {
      console.error("Could not restart login:", err);
    }
  }

  async function handleReturnToLogin() {
    await restartLogin();
  }

  async function handleTryAgain() {
    await restartLogin();
  }

  /* =========================================================
     LOADING
     ========================================================= */

  if (isLoading && !error && !urlError) {
    return <FullScreenLoader text="Preparing your dashboard..." />;
  }

  /* =========================================================
     BLOCKED / DEACTIVATED ACCOUNT
     ========================================================= */

  if (isBlocked) {
    return (
      <main className="legup-callback">
        <div className="legup-callback__background">
          <span className="legup-callback__orb legup-callback__orb--one" />
          <span className="legup-callback__orb legup-callback__orb--two" />
        </div>

        <section className="legup-callback__card">
          <div className="legup-callback__logo">
            <img src={legupLogo} alt="LEG-UP" />
          </div>

          <span className="legup-callback__eyebrow">Account access</span>

          <h1>Your account is currently deactivated</h1>

          <p className="legup-callback__description">
            Access to your LEG-UP account has been disabled by a platform
            administrator.
          </p>

          <div className="legup-callback__notice">
            <ShieldAlert size={21} />

            <div>
              <strong>Your account data is still available</strong>

              <p>
                Your existing dossiers, cases, projects, documents and messages
                have not been deleted.
              </p>
            </div>
          </div>

          <p className="legup-callback__support">
            If you believe your account was deactivated by mistake, please
            contact the LEG-UP platform administrator at{" "}
            <a href="mailto:support@renoassist.eu">support@renoassist.eu</a>.
          </p>

          <div className="legup-callback__actions">
            <button
              type="button"
              className="legup-callback__button legup-callback__button--primary"
              onClick={handleReturnToLogin}
            >
              <ArrowLeft size={18} />
              Return to login
            </button>
          </div>
        </section>
      </main>
    );
  }

  /* =========================================================
     AUTHENTICATION ERROR
     ========================================================= */

  if (error || urlError) {
    return (
      <main className="legup-callback">
        <div className="legup-callback__background">
          <span className="legup-callback__orb legup-callback__orb--one" />
          <span className="legup-callback__orb legup-callback__orb--two" />
        </div>

        <section className="legup-callback__card">
          <div className="legup-callback__logo">
            <img src={legupLogo} alt="LEG-UP" />
          </div>

          <div className="legup-callback__icon legup-callback__icon--error">
            <ShieldAlert size={32} />
          </div>

          <span className="legup-callback__eyebrow">Authentication error</span>

          <h1>We couldn't sign you in</h1>

          <p className="legup-callback__description">
            Something prevented the authentication process from completing. You
            can return to the login page or try again.
          </p>

          <div className="legup-callback__error-detail">
            {errorMessage || "An authentication error occurred."}
          </div>

          <div className="legup-callback__actions">
            <button
              type="button"
              className="legup-callback__button legup-callback__button--primary"
              onClick={handleReturnToLogin}
            >
              <ArrowLeft size={18} />
              Return to login
            </button>

            <button
              type="button"
              className="legup-callback__button legup-callback__button--secondary"
              onClick={handleTryAgain}
            >
              <RefreshCcw size={17} />
              Try again
            </button>
          </div>
        </section>
      </main>
    );
  }

  /* =========================================================
     FALLBACK
     ========================================================= */

  return <FullScreenLoader text="Preparing your dashboard..." />;
}
